/**
 * Tenant Context - Detects and manages tenant-specific information
 *
 * This context loads tenant information based on the domain/subdomain
 * the user accessed, enabling club-specific branding and content.
 *
 * Detection flow:
 * 1. Browser loads React app
 * 2. TenantContext calls GET /api/clubs/public/club-info
 * 3. Backend reads Host header → determines tenant
 * 4. Returns club info (name, colors, logo, etc.)
 * 5. React applies branding and stores tenant globally
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  console.log('🏢 TenantProvider rendering');

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyBranding = useCallback((tenantData) => {
    if (!tenantData) return;

    console.log('🎨 TenantContext: Branding disabled - will be implemented as part of club customization');

    // TODO: Club customization (branding, colors, content) will be implemented
    // as part of the club customization feature

    // Set page title
    // document.title = tenantData.display_name || tenantData.name;

    // Apply club colors as CSS variables
    // if (tenantData.colors) {
    //   const root = document.documentElement;

    //   if (tenantData.colors.primary) {
    //     root.style.setProperty('--club-primary-color', tenantData.colors.primary);
    //     root.style.setProperty('--color-primary', tenantData.colors.primary);
    //   }

    //   if (tenantData.colors.secondary) {
    //     root.style.setProperty('--club-secondary-color', tenantData.colors.secondary);
    //     root.style.setProperty('--color-secondary', tenantData.colors.secondary);
    //   }

    //   if (tenantData.colors.accent) {
    //     root.style.setProperty('--club-accent-color', tenantData.colors.accent);
    //   }

    //   console.log('✅ CSS variables applied:', {
    //     primary: tenantData.colors.primary,
    //     secondary: tenantData.colors.secondary,
    //   });
    // }

    // Set favicon (if available)
    // if (tenantData.favicon_url) {
    //   const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
    //   link.type = 'image/x-icon';
    //   link.rel = 'shortcut icon';
    //   link.href = tenantData.favicon_url;
    //   document.getElementsByTagName('head')[0].appendChild(link);
    // }

    // Set meta description
    // if (tenantData.description) {
    //   let metaDescription = document.querySelector('meta[name="description"]');
    //   if (!metaDescription) {
    //     metaDescription = document.createElement('meta');
    //     metaDescription.name = 'description';
    //     document.getElementsByTagName('head')[0].appendChild(metaDescription);
    //   }
    //   metaDescription.content = tenantData.description;
    // }
  }, []);

  const loadTenantInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🏢 TenantContext: Loading tenant information...');
      console.log('🌐 Current hostname:', window.location.hostname);

      // Call public endpoint (no auth required)
      // Browser automatically includes Host header
      const response = await fetch('/api/clubs/public/club-info', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // Check if this is platform mode (no error) or actual club not found (error)
          const errorData = await response.json();

          // Platform mode: localhost without subdomain (for platform admin)
          if (errorData.detail?.is_platform_mode) {
            console.log('🏢 TenantContext: Platform mode detected (no club context)');
            console.log('   This is expected for platform admin access via localhost');
            setTenant(null);
            setLoading(false);
            return; // Exit successfully - no error
          }

          // Actual error: club not found
          throw new Error('No club found for this domain');
        } else if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.detail?.message || errorData.detail || 'Invalid request');
        } else {
          throw new Error(`Failed to load club info: ${response.status}`);
        }
      }

      const data = await response.json();
      console.log('✅ TenantContext: Tenant info loaded:', data);

      setTenant(data);

      // Apply branding
      applyBranding(data);

      // Cache for performance and offline fallback
      try {
        localStorage.setItem('tenantInfo', JSON.stringify(data));
        localStorage.setItem('tenantInfoTimestamp', Date.now().toString());
      } catch (storageError) {
        console.warn('Failed to cache tenant info:', storageError);
      }

    } catch (err) {
      console.error('❌ TenantContext: Failed to load tenant info:', err);
      setError(err.message);

      // Try to load from cache as fallback
      try {
        const cached = localStorage.getItem('tenantInfo');
        const timestamp = localStorage.getItem('tenantInfoTimestamp');

        if (cached) {
          const cachedData = JSON.parse(cached);
          const age = Date.now() - parseInt(timestamp || '0');
          const maxAge = 24 * 60 * 60 * 1000; // 24 hours

          if (age < maxAge) {
            console.log('⚠️  TenantContext: Using cached tenant info (offline mode)');
            setTenant(cachedData);
            applyBranding(cachedData);
          } else {
            console.warn('⚠️  Cached tenant info is too old, not using');
          }
        }
      } catch (cacheError) {
        console.error('Failed to load cached tenant info:', cacheError);
      }
    } finally {
      setLoading(false);
    }
  }, [applyBranding]);

  useEffect(() => {
    loadTenantInfo();
  }, [loadTenantInfo]);

  const value = useMemo(() => {
    console.log('🔄 TenantContext: Creating new value object');
    return {
      tenant,
      loading,
      error,
      // Convenience accessors
      club_id: tenant?.club_id,
      clubName: tenant?.name,
      clubDisplayName: tenant?.display_name || tenant?.name,
      clubSlug: tenant?.slug,
      clubColors: tenant?.colors,
      clubLogo: tenant?.logo_url,
      clubDescription: tenant?.description,
      clubStatus: tenant?.status,
      // Methods
      reload: loadTenantInfo,
      isActive: tenant?.status === 'active',
    };
  }, [tenant, loading, error, loadTenantInfo]);

  // Show loading state while tenant info is loading
  if (loading && !tenant) {
    return (
      <div className="tenant-loading" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid #e0e0e0',
            borderTop: '5px solid #2c3e50',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px',
          }}></div>
          <p style={{ color: '#666', fontSize: '14px' }}>Loading club information...</p>
        </div>
      </div>
    );
  }

  // Show error state if tenant detection failed
  if (error && !tenant) {
    return (
      <div className="tenant-error" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px',
      }}>
        <div style={{
          maxWidth: '500px',
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          textAlign: 'center',
        }}>
          <div style={{
            marginBottom: '20px',
            color: '#d32f2f',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <AlertTriangle size={48} />
          </div>
          <h2 style={{ color: '#d32f2f', marginBottom: '10px' }}>Club Not Found</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>{error}</p>
          <details style={{ textAlign: 'left', marginTop: '20px', fontSize: '12px', color: '#999' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Technical Details</summary>
            <p style={{ marginTop: '10px' }}>
              <strong>Current URL:</strong> {window.location.href}<br />
              <strong>Hostname:</strong> {window.location.hostname}<br />
              <strong>Expected:</strong> A club subdomain (e.g., metro-youth-soccer.soccerplatform.com)
              or custom domain mapped to a club.
            </p>
            <p style={{ marginTop: '10px' }}>
              For development, ensure you're accessing via a subdomain like:
              <code style={{ display: 'block', marginTop: '5px', padding: '5px', backgroundColor: '#f5f5f5' }}>
                http://your-club-slug.localhost:3000
              </code>
            </p>
          </details>
          <button
            onClick={() => window.location.reload()}
            style={{
              marginTop: '20px',
              padding: '10px 20px',
              backgroundColor: '#2c3e50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
